import React, { useState, useRef, useEffect } from "react";
import { QrReader } from "react-qr-reader";
import defaultLogo from "../assets/sinin-logo.svg";
import { useNavigate, useParams } from "react-router-dom";
import CustomButton from "../components/CustomButton";
import GlassSpinner from "../components/GlassSpinner";
import ok from "../assets/sounds/ok.mp3";
import { useEmpresa } from "../context/EmpresaContext";
import { getEmpresaBrand, getEventos, validateQR } from "../services/sininApi";

function releaseCameraTracks() {
  document.querySelectorAll("video").forEach(video => {
    const stream = video.srcObject;
    if (stream?.getTracks) stream.getTracks().forEach(track => track.stop());
    video.pause?.();
    video.srcObject = null;
    video.removeAttribute("src");
    if (typeof video.load === "function") video.load();
  });
}

export default function QrScanner() {
  const { empresaId: empresaIdRuta } = useParams();
  const empresaRuta = String(empresaIdRuta || "").trim().toLowerCase();
  const [popup, setPopup] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [scanOptions, setScanOptions] = useState([]);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [readerKey, setReaderKey] = useState(0);
  const scanLock = useRef(false);
  const navigate = useNavigate();
  const { empresa, logout } = useEmpresa();
  const empresasDisponibles = scanOptions
    .filter((opt, index, all) => all.findIndex(item => item.empresa.empresaId === opt.empresa.empresaId) === index)
    .map(opt => opt.empresa);
  const eventosDisponibles = scanOptions.filter(opt => opt.empresa.empresaId === selectedEmpresaId);
  const selectedOption = scanOptions.find(o => o.key === selectedKey) || null;
  const selectedEmpresa = empresasDisponibles.find(item => item.empresaId === selectedEmpresaId) || null;
  const membresiaEmpresa = Array.isArray(empresa?.membresias)
    ? empresa.membresias.find(m => String(m.empresaId).toLowerCase() === empresaRuta)
    : null;
  const empresaScanner = membresiaEmpresa || empresa || null;
  const activeEmpresa = selectedOption?.empresa || selectedEmpresa || empresaScanner;
  const activeEvento = selectedOption?.evento || null;
  const empresaId = activeEmpresa?.empresaId;
  const empresaElegida = Boolean(activeEmpresa?.empresaId);
  const logoSrc = activeEmpresa?.logoUrl || defaultLogo;

  useEffect(() => {
    if (!empresa?.empresaId || empresa?.role !== "scanner") {
      if (empresa?.empresaId) logout();
      navigate(empresaRuta ? `/${empresaRuta.toUpperCase()}/scanner/login` : "/ADMIN", { replace: true });
      return;
    }
    if (empresaRuta && String(empresa.empresaId).toLowerCase() !== empresaRuta) {
      logout();
      navigate(`/${empresaRuta.toUpperCase()}/scanner/login`, { replace: true });
    }
  }, [empresa, empresaRuta, logout, navigate]);

  useEffect(() => {
    const cargarEventosScanner = async () => {
      const base = empresa;
      if (!base?.empresaId) return;

      setLoadingEventos(true);
      try {
        const membresias = Array.isArray(base.membresias) && base.membresias.length > 0
          ? base.membresias
          : [base];

        const grupos = await Promise.all(membresias.map(async (m) => {
          try {
            const [eventos, brandData] = await Promise.all([
              getEventos(m.empresaId, "2"),
              getEmpresaBrand(m.empresaId),
            ]);
            const brand = brandData?.empresa || {};
            return (Array.isArray(eventos) ? eventos : [])
              .filter(eventoHabilitadoParaScanner)
              .map(ev => ({
                key: `${m.empresaId}__${ev.idEvento}`,
                empresa: {
                  ...m,
                  ...brand,
                  empresaId: m.empresaId,
                  nombre: brand.nombre || m.empresaNombre || m.nombre || m.empresaId,
                  role: base.role,
                  dni: base.dni,
                  first_name: base.first_name,
                  last_name: base.last_name,
                },
                evento: ev,
              }));
          } catch {
            return [];
          }
        }));
        const opciones = grupos.flat();
        setScanOptions(opciones);
        const empresaInicial = empresaRuta || (opciones.length === 1 ? opciones[0].empresa.empresaId : "");
        if (!opciones.some(o => o.empresa.empresaId === selectedEmpresaId)) setSelectedEmpresaId(empresaInicial);
        if (!opciones.some(o => o.key === selectedKey)) setSelectedKey("");
      } finally {
        setLoadingEventos(false);
      }
    };
    cargarEventosScanner();
    // eslint-disable-next-line
  }, [empresa]);

  useEffect(() => {
    const releaseIfHidden = () => {
      if (document.hidden) releaseCameraTracks();
    };
    window.addEventListener("pagehide", releaseCameraTracks);
    document.addEventListener("visibilitychange", releaseIfHidden);
    return () => {
      releaseCameraTracks();
      window.setTimeout(releaseCameraTracks, 120);
      window.removeEventListener("pagehide", releaseCameraTracks);
      document.removeEventListener("visibilitychange", releaseIfHidden);
    };
  }, []);

  const playSound = () => {
    try {
      new Audio(ok).play().catch(() => {});
    } catch (e) {}
  };

  const stopCamera = (resetReader = true) => {
    releaseCameraTracks();
    window.setTimeout(releaseCameraTracks, 100);
    if (resetReader) setReaderKey(k => k + 1);
  };

  const cerrarScan = () => {
    setScanning(false);
    stopCamera();
    scanLock.current = false;
  };

  const closePopup = () => {
    setPopup(null);
    scanLock.current = false;
  };

  const salir = () => {
    setScanning(false);
    stopCamera(false);
    window.setTimeout(releaseCameraTracks, 300);
    logout();
    setSelectedEmpresaId("");
    setSelectedKey("");
    navigate(empresaRuta ? `/${empresaRuta.toUpperCase()}/scanner/login` : "/ADMIN", { replace: true });
  };

  const onScan = async (data) => {
    if (!data || scanLock.current) return;
    scanLock.current = true;

    const hashSeguro = data.trim();
    if (!hashSeguro) {
      playSound();
      setPopup({ color: "#ffab00", text: "QR NO RECONOCIDO ⚠" });
      return;
    }

    if (!empresaId || !activeEvento?.idEvento) {
      playSound();
      setPopup({ color: "#ffab00", text: "SIN EVENTO\n\nElegí empresa y evento antes de escanear." });
      return;
    }

    try {
      const r = await validateQR(empresaId, hashSeguro, {
        idEvento: activeEvento.idEvento,
        scannedByDni: activeEmpresa?.dni || "",
        scannedByName: `${activeEmpresa?.first_name || ""} ${activeEmpresa?.last_name || ""}`.trim(),
        scannedByRole: activeEmpresa?.role || activeEmpresa?.accessRole || "",
      });
      playSound();

      if (r.ok) {
        setPopup({
          color: "#00c853",
          text:
            `✔ ENTRADA VÁLIDA\n\n` +
            `DNI: ${r.dni}\n` +
            `${r.nombre}\n\n` +
            `${r.tanda || ""}\n` +
            `${r.tipoPago === "CORTESIA" ? "CORTESÍA" : r.tipoPago || ""}`,
        });
      } else if (r.mensaje === "QR ya utilizado") {
        setPopup({
          color: "#d50000",
          text:
            `❌ YA UTILIZADA\n\n` +
            `DNI: ${r.dni || ""}\n` +
            `${r.nombre || ""}\n\n` +
            `🕐 ${r.usadoEn || "sin fecha"}\n` +
            `${r.scannedByName ? `ESCANEÓ: ${r.scannedByName}` : ""}`,
        });
      } else {
        setPopup({ color: "#ffab00", text: `⚠ NO VÁLIDO\n\n${r.mensaje || ""}` });
      }
    } catch (e) {
      playSound();
      setPopup({ color: "#ffab00", text: "ERROR EN SERVIDOR" });
    }
  };

  return (
    <div
      style={{
        padding: 20,
        textAlign: "center",
        background: empresaElegida
          ? (activeEmpresa?.colorTicket || activeEmpresa?.colorFondo || "#25f4df")
          : "radial-gradient(circle at 16% 12%, rgba(37,244,223,.55), transparent 30%), radial-gradient(circle at 84% 86%, rgba(70,130,255,.24), transparent 34%), linear-gradient(135deg, #071416 0%, #05080a 58%, #10151a 100%)",
        color: empresaElegida ? (activeEmpresa?.colorFuente || "#111") : "#fff",
        width: "100vw",
        minHeight: "100vh",
        boxSizing: "border-box",
        transition: "background .35s ease, color .35s ease",
      }}
    >
      <img
        src={logoSrc}
        alt="logo"
        style={{
          width: 180,
          maxHeight: 95,
          objectFit: "contain",
          marginBottom: 10,
          background: "transparent",
          padding: 0,
        }}
        onError={e => {
          e.currentTarget.onerror = null;
          e.currentTarget.src = defaultLogo;
        }}
      />
      <h2>SCAN QR</h2>
      <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 4 }}>{activeEmpresa?.nombre || empresaId}</div>
      {activeEvento && (
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>
          {activeEvento.name} · {formatFecha(activeEvento.fechaEvento)}
        </div>
      )}
      {!scanning && (
        <button
          onClick={salir}
          style={{
            background: "linear-gradient(145deg, rgba(255,255,255,.56), rgba(255,255,255,.18))",
            border: "1px solid rgba(255,255,255,.72)",
            borderRadius: 25,
            padding: "8px 18px",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 900,
            marginTop: 6,
            boxShadow: "inset 0 2px 1px rgba(255,255,255,.72), inset 0 -4px 8px rgba(0,0,0,.12), 0 5px 0 rgba(0,0,0,.25), 0 10px 20px rgba(0,0,0,.18)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          }}>
          SALIR
        </button>
      )}

      {!scanning && (
        <div style={{
          maxWidth: 420,
          margin: "24px auto 0",
          background: empresaElegida ? "rgba(255,255,255,.92)" : "rgba(255,255,255,.12)",
          color: empresaElegida ? "#111" : "#f4fffe",
          border: empresaElegida ? "1px solid rgba(255,255,255,.6)" : "1px solid rgba(255,255,255,.22)",
          borderRadius: 18,
          padding: 16,
          textAlign: "left",
          boxShadow: "0 8px 20px rgba(0,0,0,.18)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
        }}>
          <label style={{ fontSize: 12, fontWeight: 900, display: "block", marginBottom: 6 }}>
            EMPRESA
          </label>
          {loadingEventos ? (
            <div style={{ textAlign: "center", padding: 14 }}>
              <GlassSpinner size={58} background={empresaElegida ? activeEmpresa?.colorTicket : "#071416"} />
              <div style={{ marginTop: 12, fontSize: 12, fontWeight: 800 }}>CARGANDO EVENTOS...</div>
            </div>
          ) : scanOptions.length === 0 ? (
            <div style={{ color: "#9b1111", fontWeight: 700, fontSize: 14, lineHeight: 1.4 }}>
              No hay eventos habilitados para escanear ahora.
              <br />
              Solo aparecen eventos desde su fecha de inicio hasta su fecha/hora de fin.
            </div>
          ) : (
            <>
              <select
                value={selectedEmpresaId}
                onChange={e => {
                  setSelectedEmpresaId(e.target.value);
                  setSelectedKey("");
                }}
                style={selectStyle}
              >
                <option value="">Seleccionar empresa</option>
                {empresasDisponibles.map(item => (
                  <option key={item.empresaId} value={item.empresaId}>
                    {(item.nombre || item.empresaId).toUpperCase()}
                  </option>
                ))}
              </select>

              <label style={{ fontSize: 12, fontWeight: 900, display: "block", margin: "16px 0 6px" }}>
                EVENTO HABILITADO
              </label>
              <select
                value={selectedKey}
                onChange={e => setSelectedKey(e.target.value)}
                disabled={!selectedEmpresaId}
                style={{ ...selectStyle, opacity: selectedEmpresaId ? 1 : 0.55 }}
              >
                <option value="">Seleccionar evento</option>
                {eventosDisponibles.map(opt => (
                  <option key={opt.key} value={opt.key}>
                    {opt.evento.name} · {formatFecha(opt.evento.fechaEvento)}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
      )}

      {/* BOTÓN INICIO */}
      {!scanning && selectedKey && (
        <div
          style={{ marginTop: 28, display: "flex", justifyContent: "center" }}
        >
          <CustomButton
            title="START SCAN"
            textColor="#fff"
            onClick={() => {
              scanLock.current = false;
              setScanning(true);
            }}
          />
        </div>
      )}

      {/* LECTOR */}
      {scanning && (
        <>
          {/* CERRAR */}
          <div
            onClick={cerrarScan}
            style={{
              position: "absolute",
              top: 10,
              right: 10,
              width: 45,
              height: 45,
              background: "#fff",
              borderRadius: "50%",
              color: "#000",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: 27,
              fontWeight: "bold",
              boxShadow: "0 0 12px rgba(0,0,0,0.4)",
              cursor: "pointer",
              zIndex: 999999,
            }}
          >
            ✕
          </div>

          <div
            style={{
              width: "100%",
              height: "65vh",
              marginTop: 20,
              overflow: "hidden",
              borderRadius: 12,
            }}
          >
            <QrReader
              key={readerKey}
              constraints={{ facingMode: "environment" }}
              onResult={(result, error) => {
                if (result?.text) onScan(result.text);
              }}
              videoContainerStyle={{ width: "100%", height: "100%" }}
              videoStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        </>
      )}

      {/* POPUP */}
      {popup && (
        <div
          onClick={closePopup}
          style={{
            position: "fixed",
            top: "10%",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "30px 24px",
            background: popup.color,
            color: "#fff",
            borderRadius: 20,
            fontSize: 22,
            lineHeight: 1.5,
            textAlign: "center",
            fontWeight: "bold",
            boxShadow: "0 0 35px rgba(0,0,0,0.60)",
            whiteSpace: "pre-line",
            width: "320px",
            minHeight: "280px",
            zIndex: 999999,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {popup.text}
        </div>
      )}
    </div>
  );
}

const selectStyle = {
  width: "100%",
  border: "1px solid rgba(0,0,0,.18)",
  borderRadius: 14,
  padding: "12px 10px",
  fontSize: 15,
  boxSizing: "border-box",
  background: "rgba(255,255,255,.82)",
};

function eventoHabilitadoParaScanner(ev) {
  const start = parseDateTime(ev.fechaEvento, "00:00");
  if (!start) return false;
  let end = parseDateTime(
    ev.fechaFinEvento || addDays(start, 1),
    ev.horaFinEvento || "03:00"
  ) || addHours(addDays(start, 1), 3);
  if (end < start) end = addHours(end, 24);
  const now = new Date();
  return now >= start && now <= end && String(ev.active || "1") === "1";
}

function parseDateTime(fecha, hora = "00:00") {
  if (!fecha) return null;
  const cleanFecha = typeof fecha === "string" ? fecha.replaceAll("/", "-") : fecha;
  const cleanHora = String(hora || "00:00").slice(0, 5);
  const d = new Date(`${cleanFecha}T${cleanHora}:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function addHours(date, hours) {
  const d = new Date(date);
  d.setHours(d.getHours() + hours);
  return d;
}

function formatFecha(fecha) {
  if (!fecha) return "";
  const parts = String(fecha).replaceAll("/", "-").split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : fecha;
}
